/**
 * Dynamic Sentry Configuration Resolver
 *
 * Environment is sourced from VITE_SENTRY_ENVIRONMENT (set per-deployment).
 * Tenant / host identification is handled by a separate 'host' tag applied
 * at Sentry.init() — see src/utils/sentry.ts. Filter in the Sentry UI:
 *   - Environment dropdown (e.g. production, staging-qa, dev)
 *   - Tag search: host:<hostname>  (e.g. host:acme.skillzen.ai)
 */

interface SentryConfig {
  dsn: string;
  environment: string;
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
  debug: boolean;
  enabled: boolean;
}

/**
 * Get Sentry configuration. Fully env-var driven so new hostnames / tenants
 * require no code changes.
 */
export function getSentryConfig(): SentryConfig {
  const hostname = window.location.hostname;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';

  const envDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  const envEnabled = import.meta.env.VITE_SENTRY_ENABLED as string | undefined;
  const envEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined;

  const config: SentryConfig = {
    dsn: envDsn || '',
    environment: envEnvironment || (isLocal ? 'local' : 'unknown'),
    tracesSampleRate: isLocal ? 1.0 : 0.3,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    debug: isLocal,
    // Default: disabled on localhost so local dev errors don't pollute Sentry;
    // enabled everywhere else (contingent on DSN being present — see isSentryEnabled).
    enabled: !isLocal,
  };

  if (envEnabled === 'true') {
    config.enabled = true;
  } else if (envEnabled === 'false') {
    config.enabled = false;
  }

  return config;
}

/**
 * Sentry is considered enabled only when both the flag is on AND a DSN is set.
 */
export function isSentryEnabled(): boolean {
  const config = getSentryConfig();
  return config.enabled && !!config.dsn;
}

/**
 * Log current Sentry configuration (for debugging).
 */
export function logSentryConfig(): void {
  const config = getSentryConfig();

  console.group('[Sentry] Configuration');
  console.log('Hostname:', window.location.hostname);
  console.log('DSN:', config.dsn ? `${config.dsn.substring(0, 20)}...` : '(not configured)');
  console.log('Environment:', config.environment);
  console.log('Enabled:', config.enabled);
  console.log('Traces Sample Rate:', config.tracesSampleRate);
  console.log('Replays On Error Rate:', config.replaysOnErrorSampleRate);
  console.log('Debug:', config.debug);
  console.groupEnd();
}
